import { useMemo } from 'react';
import { useEvents, useMasters, useStockProjection } from '../data/queries.js';
import {
  CLEANING_FREQ_HOURS,
  computeOverdueUnits,
  type StorageUnitLike,
  type TempReadingLike,
} from './overdue.js';
import { selectActiveProbeAlerts, type ProbeAlertLike } from './probeAlerts.js';
import { useOperationalIdentity } from '../auth/useOperationalIdentity.js';
import { isAssignedToOperationalIdentity } from '../auth/operationalAssignment.js';
import { isWithinExpirationLead, useExpirationPreferences } from './expirationPreferences.js';
import { useAuth } from '../auth/AuthProvider.js';

interface CleaningScheduleRow {
  id: string;
  frequency: string;
  assigned_personnel_ids?: string[] | null;
}

interface CleaningEventRow {
  payload: { schedule_id: string };
  client_ts: string;
}

export interface GlobalCounters {
  overdueTemps: number;
  overdueCleanings: number;
  expiringSoon: number;
  pendingAlerts: number;
  total: number;
}

export function useGlobalCounters(): GlobalCounters {
  const identity = useOperationalIdentity();
  const { profile } = useAuth();
  const expirationPreferences = useExpirationPreferences(profile?.active_business_id ?? null);
  const { data: units = [] } = useMasters<StorageUnitLike>('storage_units');
  const { data: schedules = [] } = useMasters<CleaningScheduleRow>('cleaning_schedules');
  const { data: foods = [] } = useMasters<{ id: string; default_uom?: string | null }>('foods');
  const { data: tempReads = [] } = useEvents('temperature_reading', 500);
  const { data: cleanEvents = [] } = useEvents('cleaning_done', 500);
  const { data: alerts = [] } = useEvents('probe_alert', 50);
  const canonicalUomByFood = useMemo(
    () => Object.fromEntries(
      foods
        .filter((food) => !!food.default_uom?.trim())
        .map((food) => [food.id, food.default_uom!.trim()]),
    ),
    [foods],
  );
  const stockProjection = useStockProjection({ canonicalUomByFood });

  return useMemo<GlobalCounters>(() => {
    const visibleUnits = units.filter((unit) => isAssignedToOperationalIdentity(
      (unit as StorageUnitLike & { assigned_personnel_ids?: string[] | null }).assigned_personnel_ids,
      identity.personnelId,
      identity.canManageOperationalWork,
    ));
    const visibleSchedules = schedules.filter((schedule) => isAssignedToOperationalIdentity(
      schedule.assigned_personnel_ids,
      identity.personnelId,
      identity.canManageOperationalWork,
    ));
    const overdueTemps = computeOverdueUnits(visibleUnits, tempReads as TempReadingLike[]);

    const lastDoneByScheduleMs = new Map<string, number>();
    for (const e of cleanEvents as CleaningEventRow[]) {
      const ts = Date.parse(e.client_ts);
      const cur = lastDoneByScheduleMs.get(e.payload.schedule_id);
      if (cur === undefined || ts > cur) lastDoneByScheduleMs.set(e.payload.schedule_id, ts);
    }
    let overdueCleanings = 0;
    const now = Date.now();
    for (const s of visibleSchedules) {
      const max = CLEANING_FREQ_HOURS[s.frequency] ?? 24;
      if (!Number.isFinite(max)) continue;
      const last = lastDoneByScheduleMs.get(s.id);
      if (!last || (now - last) / 3_600_000 >= max) overdueCleanings++;
    }

    let expiringSoon = 0;
    for (const balance of stockProjection.data.balances) {
      if (
        balance.onHandQty > 0
        && balance.expirationDate
        && isWithinExpirationLead(
          balance.expirationDate,
          expirationPreferences.alertLeadHours,
          now,
        )
      ) {
        expiringSoon++;
      }
    }

    // T-249: solo le allerte sonda realmente attive (unità non archiviata +
    // recenti + una per unità), non il conteggio grezzo degli eventi immutabili.
    const pendingAlerts = selectActiveProbeAlerts(alerts as ProbeAlertLike[], visibleUnits, now).length;
    return {
      overdueTemps,
      overdueCleanings,
      expiringSoon,
      pendingAlerts,
      total: overdueTemps + overdueCleanings + expiringSoon + pendingAlerts,
    };
  }, [expirationPreferences.alertLeadHours, identity.canManageOperationalWork, identity.personnelId, units, schedules, tempReads, cleanEvents, alerts, stockProjection.data.balances]);
}
